import React, { useState } from 'react';
import pluralize from 'pluralize';

import {
  SR_API_ACTION_HOUSEHOLD_EMAIL_DOCUMENTS,
} from 'api/actionTypes';

import { getDownloadHouseholdDocumentsUrl } from 'common/householdHelpers';

import useBranch from 'client/hooks/useBranch';
import useDispatch from 'client/hooks/useDispatch';
import postWithState from 'client/actions/postWithState';

import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrIcon from 'client/components/SrIcon';
import SrLabel from 'client/components/SrLabel/SrLabel';
import SrBox from 'client/components/SrBox';
import {
  BUTTON_THEME_GREY_ADOBE,
  BUTTON_THEME_GREEN_ADOBE,
} from 'client/components/ButtonDeprecated';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import { INCOME_VERIFICATION_DOCS_EXPIRATION_IN_DAYS } from 'common/incomeVerificationConstants';

const SR_HOUSEHOLD_DOWNLOAD_DOCUMENTS_MODAL_ID_SEND_EMAIL = 'SR_HOUSEHOLD_DOWNLOAD_DOCUMENTS_MODAL_ID_SEND_EMAIL'; // eslint-disable-line max-len
const SR_HOUSEHOLD_DOWNLOAD_DOCUMENTS_MODAL_EMAIL_SENT = 'SR_HOUSEHOLD_DOWNLOAD_DOCUMENTS_MODAL_EMAIL_SENT';
const SR_HOUSEHOLD_DOWNLOAD_DOCUMENTS_MODAL_CONFIRMATION = 'SR_HOUSEHOLD_DOWNLOAD_DOCUMENTS_MODAL_CONFIRMATION'; // eslint-disable-line max-len

const SrHouseholdDownloadDocumentsButton = ({
  listingId,
  householdId,
  hasAutomatedVOIEDocs,
  hasExpiredVOIEDocs,
  remainingDays,
}) => {
  const { appUrl, userAgent, currentUser } = useBranch({
    appUrl: ['env', 'APP_URL'],
    userAgent: ['analytics', 'userAgent'],
    currentUser: ['currentUser'],
  });
  const dispatch = useDispatch();
  const [modalId, setModalId] = useState(null);

  const shouldShowEmailDocuments = (
    currentUser.email
    && (
      userAgent.isMobile
    )
  );

  const onShowEmailDocumentsModal = () => {
    setModalId(SR_HOUSEHOLD_DOWNLOAD_DOCUMENTS_MODAL_ID_SEND_EMAIL);
  };

  const onCloseModal = () => {
    setModalId(null);
  };

  const onEmailDocuments = () => {
    dispatch(postWithState, {
      action: SR_API_ACTION_HOUSEHOLD_EMAIL_DOCUMENTS,
      payload: {
        listingId,
        householdId,
      },
    });

    setModalId(SR_HOUSEHOLD_DOWNLOAD_DOCUMENTS_MODAL_EMAIL_SENT);
  };

  const onDownloadDocuments = () => {
    const downloadDocumentsUrl = getDownloadHouseholdDocumentsUrl({
      appUrl,
      listingId,
      householdId,
      regenerateDocs: true,
    });
    global.open(downloadDocumentsUrl);
  };

  const handleDownload = () => {
    if (shouldShowEmailDocuments) {
      return onShowEmailDocumentsModal();
    }
    onDownloadDocuments();
    return onCloseModal();
  };

  const onClickDownload = () => {
    if (hasAutomatedVOIEDocs && !hasExpiredVOIEDocs) {
      return setModalId(SR_HOUSEHOLD_DOWNLOAD_DOCUMENTS_MODAL_CONFIRMATION);
    }
    return handleDownload();
  };

  return (
    <>
      <SrLabel
        color='blue300'
        borderColor='#007aff'
        label='View Full Report'
        col
        iconUrl='/images/document-icon-blue.svg'
        iconWidth='auto'
        iconHeight='24px'
        alignCenter
        justifyCenter
        onClick={onClickDownload}
      />
      {modalId === SR_HOUSEHOLD_DOWNLOAD_DOCUMENTS_MODAL_ID_SEND_EMAIL && (
        <SrSimpleMessageModal
          iconUrl='/images/send-email-green.svg'
          maxIconWidth={153}
          headline='Get Renter Docs Sent&nbsp;To&nbsp;Your&nbsp;Inbox'
          underline
          message={(
            <>
              <SrText font='inherit' color='grey300' weight='500'>
                To view the renter docs, send them to your email at
              </SrText>
              <SrText font='inherit' color='blue300' weight='600'>{` ${currentUser.email}`}</SrText>
            </>
          )}
          multiButtons={[
            {
              buttonText: (
                <SrFlex align='center' justify='center'>
                  <SrIcon className='envelope-white' marginRight='13px' />
                  Get Renter Docs
                </SrFlex>
              ),
              buttonTheme: BUTTON_THEME_GREEN_ADOBE,
              onButtonClick: onEmailDocuments,
            },
            {
              buttonText: 'Close',
              buttonTheme: BUTTON_THEME_GREY_ADOBE,
              onButtonClick: onCloseModal,
            },
          ]}
          maxWidth='375'
          theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
        />
      )}
      {modalId === SR_HOUSEHOLD_DOWNLOAD_DOCUMENTS_MODAL_EMAIL_SENT && (
        <SrSimpleMessageModal
          iconUrl='/images/checkmark-circle-large-green.svg'
          headline='Renter Docs Sent!'
          underline
          message={(
            <SrText font='inherit' color='grey300' weight='500'>
              An email was just sent to
              <SrText font='inherit' color='blue300' weight='600'>{` ${currentUser.email} `}</SrText>
              with the Renter Docs for this application attached.
            </SrText>
          )}
          multiButtons={[
            {
              buttonText: 'Got it, thanks!',
              buttonTheme: BUTTON_THEME_GREEN_ADOBE,
              onButtonClick: onCloseModal,
            },
          ]}
          maxWidth='375'
          theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
        />
      )}
      {
        modalId === SR_HOUSEHOLD_DOWNLOAD_DOCUMENTS_MODAL_CONFIRMATION && (
          <SrSimpleMessageModal
            iconUrl='/images/document-icon-expiration.svg'
            headline='Documents will expire'
            maxWidth='375'
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            onClose={onCloseModal}
            className='documentsConfirmation'
            message={(
              <SrFlex
                direction='col'
                align='center'
              >
                <SrBox
                  width='100%'
                  marginTop='16px'
                  padding='10px 0px'
                  borderRadius='14px'
                  backgroundColor='#FAE3D1'
                >
                  <SrFlex
                    direction='col'
                    align='center'
                  >
                    <SrText
                      color='orange300'
                      font='greycliff'
                      fontSize='48px'
                      weight='800'
                      lineHeight='57px'
                    >
                      {pluralize('days', remainingDays, true)}
                    </SrText>
                    <SrText
                      color='orange300'
                      font='greycliff'
                      fontSize='16px'
                      weight='700'
                      lineHeight='36px'
                    >
                      until they expire
                    </SrText>
                  </SrFlex>
                </SrBox>
                <SrBox
                  marginTop='22px'
                >
                  <SrText
                    font='greycliff'
                    fontSize='16px'
                    weight='600'
                    lineHeight='24px'
                  >
                    {`Income & credit report documents will expire after
                    ${INCOME_VERIFICATION_DOCS_EXPIRATION_IN_DAYS} days for security reasons.`}
                  </SrText>
                </SrBox>
                <SrBox
                  width='100%'
                  marginTop='28px'
                >
                  <SrLabel
                    color='blue300'
                    borderColor='#007aff'
                    label='View Full Report'
                    iconUrl='/images/document-icon-blue.svg'
                    iconWidth='auto'
                    iconHeight='24px'
                    alignCenter
                    justifyCenter
                    onClick={handleDownload}
                  />
                </SrBox>
              </SrFlex>
            )}
          />
        )
      }
    </>
  );
};

SrHouseholdDownloadDocumentsButton.displayName = 'SrHouseholdDownloadDocumentsButton';

export default SrHouseholdDownloadDocumentsButton;
